import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Plus,
  Search,
  Loader2,
  X,
  MapPin,
  Users,
  CheckCircle2,
  XCircle,
  Briefcase,
} from "lucide-react";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LieuRecrutement {
  id: string;
  libelle: string;
  ville: string;
  codePostal: string | null;
  latitude: number | null;
  longitude: number | null;
  actif: boolean;
}

export interface CampagneRow {
  id: string;
  intitule: string;
  filiere: string | null;
  poste: string | null;
  contrat: string | null;
  statut: string;
  lieuId: string | null;
  lieu_ville: string | null;
  lieu_libelle: string | null;
  dateDebut: string | null;
  dateFin: string | null;
  candidatRetenuId: string | null;
  nb_candidats: string;
}

interface CampagneDetail extends CampagneRow {
  candidats: Array<{ id: string; prenom: string | null; nom: string | null; filiere: string | null; statutPipeline: string }>;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

export const FILIERES = [
  { value: "technique", label: "Technique" },
  { value: "commercial", label: "Commercial" },
  { value: "administratif", label: "Administratif" },
];

export const CONTRATS = [
  { value: "cdi", label: "CDI" },
  { value: "cdd", label: "CDD" },
  { value: "stage", label: "Stage" },
  { value: "alternance", label: "Alternance" },
  { value: "freelance", label: "Freelance" },
];

export const STATUTS = [
  { value: "ouverte",  label: "Ouverte",  color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" },
  { value: "pourvue",  label: "Pourvue",  color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
  { value: "annulee",  label: "Annulée",  color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400" },
];

const emptyToNull = (v: unknown) => (v === "" || v === undefined ? null : v);

// ─── Mini-formulaire lieu ──────────────────────────────────────────────────────

export function AjouterLieuDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (lieu: LieuRecrutement) => void;
}) {
  const { toast } = useToast();
  const [libelle, setLibelle] = useState("");
  const [ville, setVille] = useState("");
  const [cp, setCp] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!libelle.trim() || !ville.trim()) {
      toast({ title: "Libellé et ville obligatoires", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const r = await authFetch("/api/lieux-recrutement", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ libelle, ville, codePostal: emptyToNull(cp) }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Erreur création lieu");
      onCreated(data);
      onClose();
      setLibelle(""); setVille(""); setCp("");
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajouter un bureau</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Libellé (ex : Bureau Lyon)</Label>
            <Input value={libelle} onChange={(e) => setLibelle(e.target.value)} placeholder="Bureau Lyon" data-testid="input-lieu-libelle" />
          </div>
          <div>
            <Label className="text-xs">Ville</Label>
            <Input value={ville} onChange={(e) => setVille(e.target.value)} placeholder="Lyon" data-testid="input-lieu-ville" />
          </div>
          <div>
            <Label className="text-xs">Code postal (optionnel)</Label>
            <Input value={cp} onChange={(e) => setCp(e.target.value)} placeholder="69000" data-testid="input-lieu-cp" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={loading} data-testid="btn-creer-lieu">
            {loading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            Créer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Formulaire campagne ──────────────────────────────────────────────────────

export function CampagneFormDialog({
  open,
  onClose,
  lieux,
  onRefreshLieux,
  campagne,
}: {
  open: boolean;
  onClose: () => void;
  lieux: LieuRecrutement[];
  onRefreshLieux: () => void;
  campagne?: CampagneRow | null;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const isEdit = !!campagne;

  const [intitule, setIntitule] = useState(campagne?.intitule ?? "");
  const [filiere, setFiliere] = useState(campagne?.filiere ?? "");
  const [poste, setPoste] = useState(campagne?.poste ?? "");
  const [contrat, setContrat] = useState(campagne?.contrat ?? "");
  const [lieuId, setLieuId] = useState(campagne?.lieuId ?? "");
  const [dateDebut, setDateDebut] = useState(campagne?.dateDebut ?? "");
  const [addLieuOpen, setAddLieuOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const activeLieux = lieux.filter((l) => l.actif || l.id === campagne?.lieuId);

  const submit = async () => {
    if (!intitule.trim()) {
      toast({ title: "Intitulé obligatoire", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const body = {
        intitule,
        filiere: emptyToNull(filiere),
        poste: emptyToNull(poste),
        contrat: emptyToNull(contrat),
        lieuId: emptyToNull(lieuId),
        dateDebut: emptyToNull(dateDebut),
      };
      const r = await authFetch(
        isEdit ? `/api/campagnes/${campagne!.id}` : "/api/campagnes",
        {
          method: isEdit ? "PATCH" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.message || "Erreur");
      qc.invalidateQueries({ queryKey: ["/api/campagnes"] });
      onClose();
    } catch (e: any) {
      toast({ title: "Erreur", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isEdit ? "Modifier la campagne" : "Nouvelle campagne"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Intitulé *</Label>
              <Input value={intitule} onChange={(e) => setIntitule(e.target.value)} placeholder="Technicien terrain H/F" data-testid="input-campagne-intitule" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Filière</Label>
                <Select value={filiere || "none"} onValueChange={(v) => setFiliere(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="select-campagne-filiere"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {FILIERES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Contrat</Label>
                <Select value={contrat || "none"} onValueChange={(v) => setContrat(v === "none" ? "" : v)}>
                  <SelectTrigger data-testid="select-campagne-contrat"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {CONTRATS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Poste</Label>
              <Input value={poste} onChange={(e) => setPoste(e.target.value)} placeholder="Technicien robotique" data-testid="input-campagne-poste" />
            </div>
            <div>
              <Label className="text-xs">Bureau de rattachement</Label>
              <div className="flex gap-2">
                <Select value={lieuId || "none"} onValueChange={(v) => setLieuId(v === "none" ? "" : v)}>
                  <SelectTrigger className="flex-1" data-testid="select-campagne-lieu"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {activeLieux.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.libelle} ({l.ville})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" onClick={() => setAddLieuOpen(true)} data-testid="btn-ajouter-bureau">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Date de début</Label>
              <Input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} data-testid="input-campagne-date-debut" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button onClick={submit} disabled={loading} data-testid="btn-submit-campagne">
              {loading && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {isEdit ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AjouterLieuDialog
        open={addLieuOpen}
        onClose={() => setAddLieuOpen(false)}
        onCreated={() => { onRefreshLieux(); setAddLieuOpen(false); }}
      />
    </>
  );
}

// ─── Détail campagne (drawer) ─────────────────────────────────────────────────

export function CampagneDetailDrawer({
  campagneId,
  onClose,
}: {
  campagneId: string | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [cloturerOpen, setCloturerOpen] = useState(false);
  const [selectedRetenu, setSelectedRetenu] = useState("");

  const { data: camp, isLoading } = useQuery<CampagneDetail>({
    queryKey: ["/api/campagnes", campagneId],
    queryFn: async () => (await authFetch(`/api/campagnes/${campagneId}`)).json(),
    enabled: !!campagneId,
  });

  const cloturerMutation = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`/api/campagnes/${campagneId}/cloturer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ candidat_retenu_id: selectedRetenu }),
      });
      if (!r.ok) throw new Error((await r.json()).message || "Erreur clôture");
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/campagnes"] });
      qc.invalidateQueries({ queryKey: ["/api/campagnes", campagneId] });
      setCloturerOpen(false);
      toast({ title: "Campagne clôturée" });
    },
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  const annulerMutation = useMutation({
    mutationFn: async () => {
      const r = await authFetch(`/api/campagnes/${campagneId}/annuler`, { method: "POST" });
      if (!r.ok) throw new Error((await r.json()).message || "Erreur annulation");
      return r.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/campagnes"] });
      qc.invalidateQueries({ queryKey: ["/api/campagnes", campagneId] });
      toast({ title: "Campagne annulée" });
    },
    onError: (e: Error) => toast({ title: "Erreur", description: e.message, variant: "destructive" }),
  });

  if (!campagneId) return null;

  const statutInfo = STATUTS.find((s) => s.value === camp?.statut);

  return (
    <>
      <Sheet open={!!campagneId} onOpenChange={(o) => !o && onClose()}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="campagne-drawer">
          {isLoading || !camp ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-5 py-2">
              <SheetHeader>
                <SheetTitle className="text-lg">{camp.intitule}</SheetTitle>
                <div className="flex flex-wrap gap-2 mt-1">
                  {statutInfo && <Badge className={`text-xs ${statutInfo.color}`}>{statutInfo.label}</Badge>}
                  {camp.filiere && <Badge variant="outline" className="text-xs capitalize">{camp.filiere}</Badge>}
                  {camp.contrat && <Badge variant="outline" className="text-xs uppercase">{camp.contrat}</Badge>}
                </div>
              </SheetHeader>

              <div className="grid grid-cols-2 gap-3 text-sm">
                {camp.lieu_libelle && (
                  <div className="flex items-center gap-1 text-muted-foreground col-span-2">
                    <MapPin className="w-4 h-4 shrink-0" />
                    {camp.lieu_libelle} — {camp.lieu_ville}
                  </div>
                )}
                {camp.poste && (
                  <div>
                    <span className="text-xs text-muted-foreground">Poste</span>
                    <p className="font-medium">{camp.poste}</p>
                  </div>
                )}
                {camp.dateDebut && (
                  <div>
                    <span className="text-xs text-muted-foreground">Début</span>
                    <p className="font-medium">{new Date(camp.dateDebut).toLocaleDateString("fr-FR")}</p>
                  </div>
                )}
                {camp.dateFin && (
                  <div>
                    <span className="text-xs text-muted-foreground">Fin / Clôture</span>
                    <p className="font-medium">{new Date(camp.dateFin).toLocaleDateString("fr-FR")}</p>
                  </div>
                )}
              </div>

              {/* Candidats rattachés */}
              <section>
                <p className="text-xs font-semibold uppercase text-muted-foreground mb-2 flex items-center gap-1">
                  <Users className="w-3 h-3" /> Candidats rattachés ({camp.candidats?.length ?? 0})
                </p>
                {!Array.isArray(camp.candidats) || camp.candidats.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic">Aucun candidat rattaché</p>
                ) : (
                  <div className="space-y-1">
                    {camp.candidats.map((c) => (
                      <div key={c.id} className="flex items-center justify-between text-sm py-1 px-2 rounded-md hover:bg-muted/50">
                        <span>{c.prenom} {c.nom || <span className="italic text-muted-foreground">Sans nom</span>}</span>
                        <div className="flex gap-1">
                          {c.filiere && <Badge variant="outline" className="text-xs capitalize">{c.filiere}</Badge>}
                          {camp.candidatRetenuId === c.id && (
                            <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">Retenu</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Actions */}
              {camp.statut === "ouverte" && (
                <div className="flex gap-2 pt-2 border-t">
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 text-green-700 border-green-300 hover:bg-green-50 dark:text-green-400 dark:border-green-700"
                    onClick={() => { setSelectedRetenu(""); setCloturerOpen(true); }}
                    data-testid="btn-cloturer-campagne"
                  >
                    <CheckCircle2 className="w-4 h-4" /> Clôturer (poste pourvu)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800"
                    disabled={annulerMutation.isPending}
                    onClick={() => annulerMutation.mutate()}
                    data-testid="btn-annuler-campagne"
                  >
                    <XCircle className="w-4 h-4" /> Annuler
                  </Button>
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Dialog clôture */}
      <Dialog open={cloturerOpen} onOpenChange={(o) => !o && setCloturerOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clôturer — choisir le candidat retenu</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {!Array.isArray(camp?.candidats) || camp.candidats.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucun candidat rattaché à cette campagne.</p>
            ) : (
              <Select value={selectedRetenu || "none"} onValueChange={(v) => setSelectedRetenu(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="select-candidat-retenu"><SelectValue placeholder="Choisir un candidat…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">—</SelectItem>
                  {camp.candidats.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.prenom} {c.nom || "Sans nom"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloturerOpen(false)}>Annuler</Button>
            <Button
              disabled={!selectedRetenu || cloturerMutation.isPending}
              onClick={() => cloturerMutation.mutate()}
              data-testid="btn-confirmer-cloture"
            >
              {cloturerMutation.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Confirmer la clôture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────

export default function CampagnesPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [filtStatut, setFiltStatut] = useState("all");
  const [filtFiliere, setFiltFiliere] = useState("all");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editCampagne, setEditCampagne] = useState<CampagneRow | null>(null);

  const { data: campagnes = [], isLoading } = useQuery<CampagneRow[]>({
    queryKey: ["/api/campagnes"],
    queryFn: async () => (await authFetch("/api/campagnes")).json(),
  });

  const { data: lieux = [], refetch: refetchLieux } = useQuery<LieuRecrutement[]>({
    queryKey: ["/api/lieux-recrutement"],
    queryFn: async () => (await authFetch("/api/lieux-recrutement")).json(),
  });

  const filtered = campagnes.filter((c) => {
    const matchSearch = !search ||
      c.intitule.toLowerCase().includes(search.toLowerCase()) ||
      (c.lieu_ville ?? "").toLowerCase().includes(search.toLowerCase());
    const matchStatut = filtStatut === "all" || c.statut === filtStatut;
    const matchFiliere = filtFiliere === "all" || c.filiere === filtFiliere;
    return matchSearch && matchStatut && matchFiliere;
  });

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Campagnes de recrutement</h1>
          <p className="text-sm text-muted-foreground">{campagnes.length} campagne(s)</p>
        </div>
        <Button onClick={() => { setEditCampagne(null); setFormOpen(true); }} data-testid="btn-nouvelle-campagne">
          <Plus className="w-4 h-4 mr-1" /> Créer une campagne
        </Button>
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 w-48"
            data-testid="input-search-campagnes"
          />
        </div>
        <Select value={filtStatut} onValueChange={setFiltStatut}>
          <SelectTrigger className="h-8 w-36" data-testid="select-filter-statut-camp">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous statuts</SelectItem>
            {STATUTS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtFiliere} onValueChange={setFiltFiliere}>
          <SelectTrigger className="h-8 w-36" data-testid="select-filter-filiere-camp">
            <SelectValue placeholder="Filière" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes filières</SelectItem>
            {FILIERES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {(filtStatut !== "all" || filtFiliere !== "all" || search) && (
          <Button size="sm" variant="ghost" className="h-8"
            onClick={() => { setSearch(""); setFiltStatut("all"); setFiltFiliere("all"); }}
            data-testid="btn-reset-filters-camp"
          >
            <X className="w-4 h-4 mr-1" /> Réinitialiser
          </Button>
        )}
      </div>

      {/* Tableau */}
      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-muted-foreground py-16 text-sm">
          <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-30" />
          Aucune campagne trouvée
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Intitulé</TableHead>
                <TableHead>Bureau</TableHead>
                <TableHead>Filière</TableHead>
                <TableHead>Contrat</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-center">Candidats</TableHead>
                <TableHead>Début</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => {
                const statutInfo = STATUTS.find((s) => s.value === c.statut);
                return (
                  <TableRow
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => setSelectedId(c.id)}
                    data-testid={`row-campagne-${c.id}`}
                  >
                    <TableCell className="font-medium">{c.intitule}</TableCell>
                    <TableCell>
                      {c.lieu_ville ? (
                        <span className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="w-3 h-3" />{c.lieu_ville}
                        </span>
                      ) : <span className="text-muted-foreground text-xs">—</span>}
                    </TableCell>
                    <TableCell>
                      {c.filiere
                        ? <Badge variant="outline" className="text-xs capitalize">{c.filiere}</Badge>
                        : <span className="text-muted-foreground text-xs">—</span>}
                    </TableCell>
                    <TableCell className="text-sm uppercase text-muted-foreground">{c.contrat || "—"}</TableCell>
                    <TableCell>
                      {statutInfo && <Badge className={`text-xs ${statutInfo.color}`}>{statutInfo.label}</Badge>}
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">{c.nb_candidats}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {c.dateDebut ? new Date(c.dateDebut).toLocaleDateString("fr-FR") : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Formulaire création/édition */}
      <CampagneFormDialog
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditCampagne(null); }}
        lieux={lieux}
        onRefreshLieux={() => qc.invalidateQueries({ queryKey: ["/api/lieux-recrutement"] })}
        campagne={editCampagne}
      />

      {/* Détail campagne */}
      <CampagneDetailDrawer
        campagneId={selectedId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
